import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { addInvoiceGroupMembers, getInvoiceGroupMembers, removeInvoiceGroupMembers } from "src/api/invoice";
import CustomDataTable from "../CustomDataTable";
import { Button } from "../ui/button";
import { EllipsisIcon } from "src/svg";
import CustomDropdown, { dropdownActionVariants } from "../CustomDropdown";
import CustomConfirmDialog from "../CustomConfirmDialog";
import { showSuccessToast } from "src/lib/utils";
import { useAuth } from "src/providers/AuthProvider";
import { Formik } from "formik";
import * as Yup from "yup";
import CustomModal from "../CustomModal";
import { getCustomers } from "src/api/customer";
import { getSubAccounts } from "src/api/subAccount";
import { usePortalContext } from "src/context/PortalContext";

const InvoiceGroupMembers = ({ uuid: groupUuid }) => {
	return (
		<div className="tw-gap-4 tw-grid tw-h-fit">
			<AddMembers groupUuid={groupUuid} />
			<ShowMembers groupUuid={groupUuid} />
		</div>
	);
};

const ShowMembers = ({ groupUuid }) => {
	const { user } = useAuth();

	const [{ data: rawMembers, isLoading, refetch }] = useQueries({
		queries: [
			{
				queryKey: ["invoice-group-members", groupUuid],
				queryFn: () => getInvoiceGroupMembers({ uuid: groupUuid }),
			},
		],
	});

	const { mutate } = useMutation(removeInvoiceGroupMembers, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			refetch();
		},
	});

	const columns = [
		{ name: "S/N", selector: (_, index) => index + 1, width: "100px" },
		{ name: "Name", selector: (row) => `${row.first_name} ${row.last_name}`, grow: 1.25 },
		{
			name: "Action",
			cell: (row) => (
				<CustomDropdown
					trigger={
						<Button variant="hover">
							<EllipsisIcon />
						</Button>
					}
					actions={[
						{
							label: (
								<CustomConfirmDialog
									trigger={<span className={dropdownActionVariants({ variant: "destructive" })}>Remove Member</span>}
									mutateFn={() =>
										mutate({
											uuid: groupUuid,
											data: {
												[user?.organization_type_name === "Schools" ? "subaccounts" : "customers"]: [row.id],
											},
										})
									}
								/>
							),
							asChild: true,
						},
					]}
				/>
			),
			grow: 0.5,
		},
	];

	const members = rawMembers?.data?.data;
	const totalRow = rawMembers?.data?.total;

	return (
		<div>
			<p className="tw-font-semibold tw-text-lg tw-mb-2">Members</p>
			<CustomDataTable columns={columns} data={members} progressPending={isLoading} paginationTotalRows={totalRow} />
		</div>
	);
};

const AddMembers = ({ groupUuid }) => {
	return (
		<div className="tw-grid">
			<CustomModal title="Add Members" trigger={<Button className="tw-ml-auto">Add Members</Button>}>
				<Body groupUuid={groupUuid} />
			</CustomModal>
		</div>
	);
};

const Body = ({ groupUuid }) => {
	const closePortal = usePortalContext();
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const { mutate } = useMutation(addInvoiceGroupMembers, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			closePortal();
			queryClient.invalidateQueries(["invoice-group-members"]);
		},
	});

	const { data, isLoading } = useQuery({
		queryKey: ["group-members-list"],
		queryFn: user?.organization_type_name === "Schools" ? () => getSubAccounts({}) : () => getCustomers({}),
	});

	const members = data?.data?.data;
	const totalRow = data?.data?.total;

	const columns = [
		{ name: "S/N", selector: (_, index) => index + 1, width: "100px" },
		{ name: "Name", selector: (row) => `${row.first_name} ${row.last_name}`, grow: 1.25 },
	];

	const key = user?.organization_type_name === "Schools" ? "subaccounts" : "customers";

	const initialValues = {
		[key]: [],
	};

	const validationSchema = Yup.object().shape({
		[key]: Yup.array().of(Yup.string().required()),
	});
	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate({ uuid: groupUuid, data: values })}>
			{({ values, setFieldValue, handleSubmit }) => (
				<form onSubmit={handleSubmit} className="tw-grid">
					<Button className="tw-ml-auto !tw-py-2.5">Submit</Button>
					<CustomDataTable
						columns={columns}
						data={members}
						progressPending={isLoading}
						paginationTotalRows={totalRow}
						selectableRows
						containerClassName="!tw-shadow-none"
						customStyles={{
							table: {
								paddingTop: 0,
							},
							headRow: {
								style: {
									paddingInline: "0",
								},
							},
							rows: {
								style: {
									paddingInline: "0",
								},
							},
						}}
						onSelectedRowsChange={({ selectedRows }) =>
							setFieldValue(
								key,
								selectedRows?.map(({ id }) => id),
							)
						}
					/>
				</form>
			)}
		</Formik>
	);
};
export default InvoiceGroupMembers;
